import { Component, OnInit, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { UntypedFormGroup, Validators, UntypedFormBuilder } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import Swal from 'sweetalert2';

import { TranslationService } from '@app/core/_base/layout';
import { LocalStorageKeys } from '@app/core/_constant/local-storage.constants';
import { User, AuthService } from '@app/core/auth';
import { PermissionService } from '@app/core/auth/services/permission.service';
import { UserRoleConstants } from '@app/core/staff/store/staff.model';
import { environment } from '@app/env-service';
import { FeatureToggleService } from '@app/shared/services/feature-toggle.service';
import { TenantInfoService } from '@app/shared/services/tenant-info.service';

interface LanguageFlag {
  lang :string;
  name :string;
  flag :string;
  active ?:boolean;
}

@Component({
  selector: 'cnc-auth-sign-in',
  templateUrl: './auth-sign-in.component.html',
  styleUrls: ['./auth-sign-in.component.scss'],
  host: { class: 'lum-login-body' },
})
export class AuthSignInComponent implements OnInit, OnDestroy {
  account :User = new User();
  loginForm :UntypedFormGroup;
  loading :boolean = false;
  loggedIn :boolean = false;
  today :number = Date.now();

  languages :LanguageFlag[] = [
    {
      lang: 'en',
      name: 'English',
      flag: './assets/media/flags/012-uk.svg',
    },
    // {
    // 	lang: 'ch',
    // 	name: 'Mandarin',
    // 	flag: './assets/media/flags/015-china.svg'
    // },
    {
      lang: 'es',
      name: 'Spanish',
      flag: './assets/media/flags/016-spain.svg',
    },
    // {
    // 	lang: 'jp',
    // 	name: 'Japanese',
    // 	flag: './assets/media/flags/014-japan.svg'
    // },
    // {
    // 	lang: 'de',
    // 	name: 'German',
    // 	flag: './assets/media/flags/017-germany.svg'
    // },
    // {
    // 	lang: 'fr',
    // 	name: 'French',
    // 	flag: './assets/media/flags/019-france.svg'
    // },
  ];

  private onDestroy$ = new Subject();

  constructor(
    private authService :AuthService,
    private activeRoute :ActivatedRoute,
    private router :Router,
    private fb :UntypedFormBuilder,
    private cd :ChangeDetectorRef,
    private translate :TranslateService,
    private translation :TranslationService,
    private ft :FeatureToggleService,
    private route :ActivatedRoute,
    private permissionSvc :PermissionService,
    private tenantInfoService :TenantInfoService,
  ) {}

  ngOnInit() {
    this.redirectToEnterpriseLoginIfRequired();

    const lang = this.translation.getSelectedLanguage() || 'en';
    this.loginForm = this.fb.group({
      email: ['', Validators.compose([Validators.required, Validators.email])],
      password: ['', Validators.compose([Validators.required])],
      language: [lang, Validators.required],
    });

    this.loginForm.valueChanges.subscribe((value) => {
      this.translation.setLanguage(value.language);
    });

    this.attemptToLoginWithAdminToken();
    this.attemptToAutoLoginWithB2bPortalToken();
  }

  redirectToEnterpriseLoginIfRequired() :void {
    // If the tenant requires enterprise login, and the user does not have a token already issued,
    // redirect to the enterprise login page
    combineLatest([
      this.tenantInfoService.getTenantInfo(),
      this.route.queryParamMap,
    ])
      .pipe(
        map(([tenantInfo, params]) => ({
          enterpriseLoginUrl: tenantInfo.enterprise_login_url,
          hasToken: params.has('token'),
          hasAdminToken: params.has('admin_token'),
        })),
      )
      .subscribe(({ enterpriseLoginUrl, hasToken, hasAdminToken }) => {
        if (enterpriseLoginUrl && !hasToken && !hasAdminToken) {
          window.location.href = enterpriseLoginUrl;
        }
      });
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  attemptToAutoLoginWithB2bPortalToken() {
    this.activeRoute.queryParams.subscribe((params :any) => {
      if (!params || !params.token) {
        return;
      }
      this.authService.loginWithB2bPortalToken(params.token).subscribe(
        (data :any) => {
          this.#redirectLoggedInUser(data);
        },
        (_) => {
          this.router.navigate([], { replaceUrl: true });
        },
      );
    });
  }

  attemptToLoginWithAdminToken() {
    this.activeRoute.queryParams.subscribe((params :any) => {
      if (!params || !params.admin_token) {
        return;
      }
      this.authService.loginWithAdminToken(params.admin_token).subscribe(
        (data :any) => {
          this.#redirectLoggedInUser(data);
        },
        (_) => {
          this.router.navigate([], { replaceUrl: true });
        },
      );
    });
  }

  animateFormOut() :void {
    this.loggedIn = true;
  }

  handleSubmit(e :SubmitEvent) {
    e.preventDefault();

    this.#submit();
  }

  isControlHasError(controlName :string, validationType :string) :boolean {
    const control = this.loginForm.controls[controlName];
    if (!control) {
      return false;
    }
    const result =
      control.hasError(validationType) && (control.dirty || control.touched);

    return result;
  }

  #submit() {
    if (this.loginForm.invalid) {
      return;
    }
    const credentials = this.loginForm.value;
    this.loading = true;
    this.authService.login(credentials).subscribe(
      (data :any) => {
        this.#redirectLoggedInUser(data);
      },
      (errorResp :any) => {
        Swal.fire({
          title: this.translate.instant('MESSAGES.INVALID_LOGIN.TITLE'),
          text: errorResp.error.message,
          type: 'error',
          confirmButtonText: this.translate.instant('BUTTONS.OKAY'),
        }).then((data) => {
          this.loading = false;
          this.cd.markForCheck();
        });
      },
    );
  }

  #redirectLoggedInUser(data :any) {
    this.animateFormOut();
    const lang = this.authService.getLanguage();
    this.translation.setLanguage(lang);
    this.authService.setLanguage(lang).subscribe();

    this.permissionSvc
      .getDisplayPageSettings()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((resp :any) => {
        localStorage.setItem(
          LocalStorageKeys.DISPLAY_PAGE_SETTINGS,
          JSON.stringify(resp ?? {}),
        );

        this.loading = false;

        const returnTo = this.route.snapshot.queryParams['returnTo'];
        if (returnTo) {
          this.router.navigate([returnTo]);

          return;
        }

        switch (data.user.role) {
          case UserRoleConstants.SUPER_ADMIN:
            window.location.href = '/revenue';
            break;
          case UserRoleConstants.SALES_MEMBER:
            window.location.href = '/sales/crm/companies';
            break;
          default:
            window.location.href = '/inventory/skus';
        }
      });
  }
}
